import { useState } from "react";
import { toast } from "sonner";
import { Crosshair, MapPin, Thermometer, Mountain, Gauge, LocateFixed } from "lucide-react";
import { useCalculatorStore } from "@/store/calculatorStore";
import { airDensity, CONVERT } from "@/utils/ballisticsMath";
import { reverseGeocode, forwardGeocode, fetchWeather } from "@/utils/geo";
import { useT } from "@/i18n/translations";

const SegToggle = ({ options, value, onChange, testId }) => (
  <div
    className="flex items-center rounded-md border border-dase-border bg-dase-input p-0.5"
    data-testid={testId}
  >
    {options.map((o) => (
      <button
        key={o.id}
        data-testid={`${testId}-${o.id}`}
        onClick={() => onChange(o.id)}
        className={`px-3 py-1.5 text-[11px] font-bold tracking-widest uppercase rounded transition-colors ${
          value === o.id ? "bg-dase-red text-white" : "text-zinc-400 hover:text-zinc-200"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const EnvChip = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 border-l border-dase-border pl-3">
    <Icon className="h-3.5 w-3.5 text-dase-red" strokeWidth={2.2} />
    <div className="leading-tight">
      <div className="text-[9px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-xs font-bold text-zinc-200">{value}</div>
    </div>
  </div>
);

const LocationControl = () => {
  const t = useT();
  const language = useCalculatorStore((s) => s.language);
  const location = useCalculatorStore((s) => s.location);
  const setLocation = useCalculatorStore((s) => s.setLocation);
  const setField = useCalculatorStore((s) => s.setField);
  const [busy, setBusy] = useState(false);

  const applyWeather = async (lat, lon, elevation) => {
    if (elevation != null && !Number.isNaN(elevation)) {
      setField("altitude", Math.max(0, Math.round(elevation)));
    }
    try {
      const w = await fetchWeather(lat, lon);
      if (w.tempC != null) setField("tempC", Math.round(w.tempC * 10) / 10);
      if (w.pressureHpa != null) setField("pressureHpa", Math.round(w.pressureHpa * 10) / 10);
      toast.success(t("toast.weatherSet"));
    } catch {
      toast.error(t("toast.weatherFail"));
    }
  };

  const useGps = () => {
    if (!navigator.geolocation) {
      toast.error(t("toast.gpsError"));
      return;
    }
    setBusy(true);
    toast(t("toast.gpsWait"));
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, altitude } = pos.coords;
        try {
          const name = await reverseGeocode(latitude, longitude, language);
          setLocation(name);
        } catch {
          setLocation(`${latitude.toFixed(3)}, ${longitude.toFixed(3)}`);
        }
        await applyWeather(latitude, longitude, altitude);
        setBusy(false);
      },
      () => {
        setBusy(false);
        toast.error(t("toast.gpsError"));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const searchByName = async () => {
    const q = location.trim();
    if (!q) return;
    setBusy(true);
    try {
      const g = await forwardGeocode(q, language);
      if (!g) {
        toast.error(t("toast.locNotFound"));
        setBusy(false);
        return;
      }
      setLocation(g.name);
      await applyWeather(g.lat, g.lon, g.elevation);
    } catch {
      toast.error(t("toast.weatherFail"));
    }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-1 rounded-md border border-dase-border bg-dase-panel px-2 py-1">
      <MapPin className="h-3.5 w-3.5 shrink-0 text-dase-red" strokeWidth={2.2} />
      <input
        type="text"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") searchByName();
        }}
        placeholder={t("header.setLocation")}
        title={t("header.searchHint")}
        data-testid="location-input"
        className="w-36 bg-transparent text-xs font-semibold text-zinc-200 outline-none placeholder:text-zinc-600"
      />
      <button
        onClick={useGps}
        disabled={busy}
        data-testid="location-gps-btn"
        title={t("header.gps")}
        className="rounded p-1 text-zinc-400 transition-colors hover:text-dase-red disabled:opacity-50"
      >
        <LocateFixed className={`h-3.5 w-3.5 ${busy ? "animate-pulse text-dase-red" : ""}`} />
      </button>
    </div>
  );
};

export const Header = () => {
  const t = useT();
  const tempC = useCalculatorStore((s) => s.tempC);
  const altitude = useCalculatorStore((s) => s.altitude);
  const pressureHpa = useCalculatorStore((s) => s.pressureHpa);
  const unitSystem = useCalculatorStore((s) => s.unitSystem);
  const setUnitSystem = useCalculatorStore((s) => s.setUnitSystem);
  const language = useCalculatorStore((s) => s.language);
  const setLanguage = useCalculatorStore((s) => s.setLanguage);
  const rho = airDensity(tempC, altitude, pressureHpa);

  const tempStr =
    unitSystem === "imperial" ? `${CONVERT.cToF(tempC).toFixed(0)}°F` : `${tempC.toFixed(0)}°C`;
  const altStr =
    unitSystem === "imperial" ? `${Math.round(altitude * 3.28084)} ft` : `${Math.round(altitude)} m`;

  return (
    <header
      className="dase-header sticky top-0 z-40 border-b border-dase-border bg-dase-bg/90 backdrop-blur-md"
      data-testid="app-header"
    >
      <div className="dase-header__inner mx-auto flex h-full w-full max-w-[1480px] items-center justify-between gap-3 px-4 md:px-6 lg:px-8">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md border border-dase-red/60 bg-dase-red/10">
          <Crosshair className="h-5 w-5 text-dase-red" strokeWidth={2.4} />
        </div>
        <div className="leading-none">
          <h1 className="text-base font-extrabold uppercase tracking-tight text-zinc-100 sm:text-lg">
            DASE <span className="text-dase-red">BALLISTICS</span> LASKURI
          </h1>
          <p className="mt-1 hidden text-[9px] font-semibold uppercase tracking-[0.2em] text-zinc-500 sm:block">
            {t("app.subtitle")}
          </p>
        </div>
      </div>

      <div className="hidden items-center gap-3 lg:flex">
        <LocationControl />
        <div className="hidden items-center gap-3 xl:flex">
          <EnvChip icon={Thermometer} label={t("header.temp")} value={tempStr} />
          <EnvChip icon={Mountain} label={t("header.altitude")} value={altStr} />
          <EnvChip icon={Gauge} label={t("header.airDensity")} value={`${rho.toFixed(3)} kg/m³`} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <SegToggle
          options={[
            { id: "fi", label: "FI" },
            { id: "en", label: "EN" },
          ]}
          value={language}
          onChange={setLanguage}
          testId="language-toggle"
        />
        <SegToggle
          options={[
            { id: "metric", label: "METRIC" },
            { id: "imperial", label: "IMPERIAL" },
          ]}
          value={unitSystem}
          onChange={setUnitSystem}
          testId="unit-system"
        />
      </div>
      </div>
    </header>
  );
};

export default Header;
